// src/pages/Admin/Abonnement/AbonnementPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import Button from "./../../../components/Button/Button";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import ParrainageSection from "./ParrainageSection";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { FaFileSignature } from "react-icons/fa";

/** ---------------------------
 * Helpers dates
 * --------------------------- */
const parseDateSafe = (d) => {
  if (!d) return null;
  // Accepte "YYYY-MM-DD" ou ISO
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d)
    ? new Date(`${d}T00:00:00`)
    : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const dayAnchor = (d) => {
  // Ancre à midi pour limiter les effets DST/minuit
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return x;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const fmtDate = (d) => {
  const dt = parseDateSafe(d);
  if (!dt) return "—";
  return dt.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

/** ---------------------------
 * Helpers money (✅ 2 décimales + € après, même si string)
 * --------------------------- */
const moneyToNumber = (v) => {
  if (typeof v === "number") return v;
  if (v === null || v === undefined) return NaN;

  let s = String(v).trim();
  if (!s) return NaN;

  // retire espaces + éventuels libellés
  s = s.replace(/\s/g, "").replace(/€|eur/gi, "");

  // garde chiffres + séparateurs + signe
  s = s.replace(/[^0-9,.\-]/g, "");

  // si on a "," et ".", on suppose "." = milliers, "," = décimales
  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",") && !s.includes(".")) {
    s = s.replace(",", ".");
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const euro = (v) => {
  const n = moneyToNumber(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const isQuotePending = (sub) => {
  const key = normalizeKey(sub?.status);

  return (
    key.includes("devis") || key.includes("signature") || key.includes("quote")
  );
};

/** ---------------------------
 * Badges
 * --------------------------- */
const normalizeKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const statusBadge = (s) => {
  const raw = String(s || "").trim();
  const key = normalizeKey(raw);

  if (!key) return { label: "—", cls: "bg-slate-500" };

  if (
    key.includes("devis") ||
    key.includes("signature") ||
    key.includes("quote")
  ) {
    return { label: "Devis à signer", cls: "bg-purple-600" };
  }

  // Abonné
  if (
    key === "active" ||
    key === "abonne" ||
    key.includes("abonn") ||
    key === "subscribed"
  ) {
    return { label: "Abonné", cls: "bg-emerald-600" };
  }

  // En attente
  if (key === "pending" || key.includes("attente")) {
    return { label: "En attente", cls: "bg-amber-500" };
  }

  // Terminé / Expiré / Annulé
  if (
    key === "expired" ||
    key.includes("expire") ||
    key.includes("termine") ||
    key.includes("annule") ||
    key === "cancelled" ||
    key === "canceled"
  ) {
    return { label: "Terminé", cls: "bg-rose-600" };
  }

  if (key.includes("pause")) return { label: "En pause", cls: "bg-violet-600" };
  if (key.includes("progress") || key.includes("cours"))
    return { label: "En cours", cls: "bg-blue-600" };

  return { label: raw || "—", cls: "bg-slate-500" };
};

const periodicityBadge = (p) => {
  const key = normalizeKey(p);

  if (
    key === "yearly" ||
    key === "annual" ||
    key === "annuel" ||
    key.includes("ann")
  ) {
    return { label: "Annuel", cls: "bg-sky-600" };
  }

  if (key === "monthly" || key === "mensuel" || key.includes("mens")) {
    return { label: "Mensuel", cls: "bg-indigo-600" };
  }

  if (key === "trimestrial" || key === "quarterly" || key.includes("trimes")) {
    return { label: "Trimestriel", cls: "bg-cyan-600" };
  }

  return { label: p || "—", cls: "bg-slate-600" };
};

const parrainageBadge = () => ({ label: "Parrainage", cls: "bg-violet-600" });

/** ---------------------------
 * Progression
 * --------------------------- */
const computeProgress = (start_date, end_date) => {
  const s0 = parseDateSafe(start_date);
  const e0 = parseDateSafe(end_date);

  if (!s0 || !e0) {
    return {
      valid: false,
      percent: 0,
      daysRemaining: null,
      colorCls: "bg-slate-400",
      text: "—",
    };
  }

  const today = dayAnchor(new Date());
  const s = dayAnchor(s0);
  const e = dayAnchor(e0);

  const totalMs = e.getTime() - s.getTime();
  const daysRemaining = Math.ceil((e.getTime() - today.getTime()) / 86400000);

  let percent = 0;
  if (totalMs <= 0) {
    percent = today.getTime() >= e.getTime() ? 100 : 0;
  } else if (today.getTime() <= s.getTime()) {
    percent = 0;
  } else if (today.getTime() >= e.getTime()) {
    percent = 100;
  } else {
    percent = ((today.getTime() - s.getTime()) / totalMs) * 100;
  }
  percent = clamp(Math.round(percent), 0, 100);

  let colorCls = "bg-emerald-600";
  if (today.getTime() > e.getTime()) colorCls = "bg-rose-600";
  else if (daysRemaining <= 30) colorCls = "bg-amber-500";

  let text = `${percent}%`;
  if (today.getTime() > e.getTime()) text = `Expiré • ${percent}%`;
  else if (today.getTime() < s.getTime())
    text = `Démarre bientôt • ${percent}%`;
  else text = `J-${Math.max(0, daysRemaining)} • ${percent}%`;

  return { valid: true, percent, daysRemaining, colorCls, text };
};

function ProgressGauge({ prog }) {
  if (!prog?.valid) return <span className="italic text-slate-400">—</span>;

  return (
    <div className="min-w-[170px]">
      <div className="flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-300 mb-1">
        <span className="font-medium">{prog.text}</span>
        <span className="tabular-nums">{prog.percent}%</span>
      </div>

      <div className="h-2.5 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full ${prog.colorCls} rounded-full transition-[width] duration-500`}
          style={{ width: `${prog.percent}%` }}
        />
      </div>
    </div>
  );
}

/** ---------------------------
 * Page
 * --------------------------- */
export default function AbonnementPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const ASSO_ID = process.env.REACT_APP_ASSO_ID;

  const [abonnementData, setAbonnementData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAbonnement = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://api.monappliclub.com/api/subscriptions/subscriptionsByAssociation/${ASSO_ID}`,
      );
      setAbonnementData(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la récupération des abonnements");
    } finally {
      setLoading(false);
    }
  }, [ASSO_ID]);

  useEffect(() => {
    fetchAbonnement();
  }, [fetchAbonnement]);

  const subs = useMemo(() => abonnementData ?? [], [abonnementData]);

  const isParrainage = useCallback((sub) => {
    const amt = moneyToNumber(sub?.total_amount);
    return Number.isFinite(amt) && amt < 0;
  }, []);

  const { abonnements, devis, parrainages } = useMemo(() => {
    const a = [];
    const d = [];
    const p = [];

    for (const s of subs) {
      if (isParrainage(s)) {
        p.push(s);
      } else if (isQuotePending(s)) {
        d.push(s);
      } else {
        a.push(s);
      }
    }

    return { abonnements: a, devis: d, parrainages: p };
  }, [subs, isParrainage]);

  const hasAny =
    abonnements.length > 0 || devis.length > 0 || parrainages.length > 0;

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* En-tête page */}
        <HeaderTitle
          role="Administrateur"
          title="Abonnement & Parrainage"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau dégradé : infos */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h3 className="text-lg sm:text-xl font-semibold text-center">
                Détails de votre abonnement
              </h3>
              <div className="mt-2 text-sm/6 max-w-3xl mx-auto text-center space-y-1">
                <p>
                  Visualisez vos{" "}
                  <span className="font-semibold">abonnements</span> et vos{" "}
                  <span className="font-semibold">
                    réductions de parrainage
                  </span>
                  .
                </p>
                <p>
                  Les <span className="font-semibold">montants négatifs</span>{" "}
                  correspondent à des{" "}
                  <span className="font-semibold">parrainages</span>.
                </p>
              </div>
              <div className="mt-4 flex justify-center">
                <Button
                  text="Actualiser les données"
                  onClick={fetchAbonnement}
                  className="!bg-white !text-slate-900 hover:!bg-white/90 !rounded-xl !shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Skeleton */}
            {loading && (
              <div className="space-y-3 mt-5">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && !hasAny && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun abonnement trouvé
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Contactez le support si vous pensez que c’est une erreur.
                </p>
              </div>
            )}

            {!loading && devis.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Devis à signer
                    </h4>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Retrouvez ici les devis en attente pour renouveler votre
                      abonnement.
                    </p>
                  </div>

                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {devis.length} devis
                  </span>
                </div>

                {/* TABLE DESKTOP */}
                <div className="hidden md:block mt-3">
                  <div className="overflow-x-auto rounded-xl border border-purple-200 dark:border-purple-900/50 bg-purple-50/40 dark:bg-purple-950/10">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-purple-50 dark:bg-purple-950/30 uppercase tracking-wide text-xs">
                          {[
                            "Produit",
                            "Montant",
                            "Périodicité",
                            "Début prévu",
                            "Fin prévue",
                            "Statut",
                            "Devis",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-purple-100 dark:divide-purple-900/40 text-sm">
                        {devis.map((sub) => {
                          const s = statusBadge(sub.status);
                          const per = periodicityBadge(sub.periodicity);

                          return (
                            <tr
                              key={`devis-${sub.id}`}
                              className="transition-colors hover:bg-white/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                                {sub.product || "—"}
                              </td>

                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {euro(sub.total_amount)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${per.cls}`}
                                >
                                  {per.label}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                {fmtDate(sub.start_date)}
                              </td>
                              <td className="px-4 py-3">
                                {fmtDate(sub.end_date)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${s.cls}`}
                                >
                                  {s.label}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                {sub.drive_quote_link ? (
                                  <a
                                    href={sub.drive_quote_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white/80 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-50 dark:border-purple-800 dark:bg-slate-900/40 dark:text-purple-300 dark:hover:bg-purple-950/30"
                                  >
                                    <FaFileSignature />
                                    Signer le devis ↗
                                  </a>
                                ) : (
                                  <span className="italic text-slate-400">
                                    Devis non disponible
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* CARTES MOBILE */}
                <div className="md:hidden mt-3 space-y-3">
                  {devis.map((sub) => {
                    const s = statusBadge(sub.status);
                    const per = periodicityBadge(sub.periodicity);

                    return (
                      <div
                        key={`card-devis-${sub.id}`}
                        className="rounded-xl border border-purple-200 dark:border-purple-900/50 bg-purple-50/60 dark:bg-purple-950/10 p-4 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {sub.product || "—"}
                            </h3>

                            <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                              Renouvellement à valider
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-1">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${per.cls}`}
                            >
                              {per.label}
                            </span>

                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${s.cls}`}
                            >
                              {s.label}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
                          <div className="flex items-center justify-between">
                            <span className="opacity-70">Montant</span>
                            <span className="font-semibold">
                              {euro(sub.total_amount)}
                            </span>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <span className="opacity-70">Début prévu</span>
                            <span>{fmtDate(sub.start_date)}</span>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <span className="opacity-70">Fin prévue</span>
                            <span>{fmtDate(sub.end_date)}</span>
                          </div>
                        </div>

                        <div className="mt-4">
                          {sub.drive_quote_link ? (
                            <a
                              href={sub.drive_quote_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-50 dark:border-purple-800 dark:bg-slate-900/40 dark:text-purple-300 dark:hover:bg-purple-950/30"
                            >
                              <FaFileSignature />
                              Signer le devis ↗
                            </a>
                          ) : (
                            <span className="italic text-slate-400">
                              Devis non disponible
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* =======================
                SECTION ABONNEMENTS
               ======================= */}
            {!loading && abonnements.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Abonnements
                  </h4>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {abonnements.length} élément(s)
                  </span>
                </div>

                {/* TABLE DESKTOP */}
                <div className="hidden md:block mt-3">
                  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {[
                            "Produit",
                            "Montant",
                            "Périodicité",
                            "Début",
                            "Fin",
                            "Statut",
                            "Progression",
                            "Facture",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {abonnements.map((sub) => {
                          const s = statusBadge(sub.status);
                          const per = periodicityBadge(sub.periodicity);
                          const prog = computeProgress(
                            sub.start_date,
                            sub.end_date,
                          );

                          return (
                            <tr
                              key={sub.id}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                                {sub.product || "—"}
                              </td>

                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {euro(sub.total_amount)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${per.cls}`}
                                >
                                  {per.label}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                {fmtDate(sub.start_date)}
                              </td>
                              <td className="px-4 py-3">
                                {fmtDate(sub.end_date)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${s.cls}`}
                                >
                                  {s.label}
                                </span>
                              </td>

                              {/* ✅ Jauge */}
                              <td className="px-4 py-3">
                                <ProgressGauge prog={prog} />
                              </td>

                              <td className="px-4 py-3">
                                {sub.drive_invoice_link ? (
                                  <a
                                    href={sub.drive_invoice_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                                  >
                                    Voir la facture ↗
                                  </a>
                                ) : (
                                  <span className="italic text-slate-400">
                                    Non disponible
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* CARTES MOBILE */}
                <div className="md:hidden mt-3 space-y-3">
                  {abonnements.map((sub) => {
                    const s = statusBadge(sub.status);
                    const per = periodicityBadge(sub.periodicity);
                    const prog = computeProgress(sub.start_date, sub.end_date);

                    return (
                      <div
                        key={`card-ab-${sub.id}`}
                        className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                            {sub.product || "—"}
                          </h3>
                          <div className="flex items-center gap-1">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${per.cls}`}
                            >
                              {per.label}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${s.cls}`}
                            >
                              {s.label}
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                          <div className="flex items-center justify-between">
                            <span className="opacity-70">Montant</span>
                            <span className="font-semibold">
                              {euro(sub.total_amount)}
                            </span>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <span className="opacity-70">Début</span>
                            <span>{fmtDate(sub.start_date)}</span>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <span className="opacity-70">Fin</span>
                            <span>{fmtDate(sub.end_date)}</span>
                          </div>

                          {/* ✅ Jauge */}
                          <div className="mt-3">
                            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                              Progression
                            </div>
                            <ProgressGauge prog={prog} />
                          </div>
                        </div>

                        <div className="mt-3">
                          {sub.drive_invoice_link ? (
                            <a
                              href={sub.drive_invoice_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                            >
                              Voir la facture ↗
                            </a>
                          ) : (
                            <span className="italic text-slate-400">
                              Facture non disponible
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* =======================
    SECTION PARRAINAGES
   ======================= */}
            {!loading && parrainages.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Parrainages (réductions -10% sur abonnement annuel )
                  </h4>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {parrainages.length} élément(s)
                  </span>
                </div>

                {/* TABLE DESKTOP (✅ sans périodicité / progression / facture, ✅ AVEC statut) */}
                <div className="hidden md:block mt-3">
                  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {["Type", "Montant", "Début", "Fin", "Statut"].map(
                            (h) => (
                              <th
                                key={h}
                                className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                              >
                                {h}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {parrainages.map((sub) => {
                          const p = parrainageBadge();
                          const s = statusBadge(sub.status);

                          return (
                            <tr
                              key={`par-${sub.id}`}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${p.cls}`}
                                    title="Réduction obtenue via parrainage"
                                  >
                                    {p.label}
                                  </span>

                                  {sub.product ? (
                                    <span className="text-slate-700 dark:text-slate-300">
                                      {sub.product}
                                    </span>
                                  ) : null}
                                </div>
                              </td>

                              <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                                {euro(sub.total_amount)}
                              </td>

                              <td className="px-4 py-3">
                                {fmtDate(sub.start_date)}
                              </td>
                              <td className="px-4 py-3">
                                {fmtDate(sub.end_date)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${s.cls}`}
                                >
                                  {s.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* CARTES MOBILE (✅ sans périodicité / progression / facture, ✅ AVEC statut) */}
                <div className="md:hidden mt-3 space-y-3">
                  {parrainages.map((sub) => {
                    const p = parrainageBadge();
                    const s = statusBadge(sub.status);

                    return (
                      <div
                        key={`card-par-${sub.id}`}
                        className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              Parrainage
                            </h3>
                            {sub.product && (
                              <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                                {sub.product}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col items-end gap-1">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${p.cls}`}
                            >
                              {p.label}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${s.cls}`}
                            >
                              {s.label}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 text-sm text-slate-700 dark:text-slate-300">
                          <div className="flex items-center justify-between">
                            <span className="opacity-70">Montant</span>
                            <span className="font-semibold">
                              {euro(sub.total_amount)}
                            </span>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <span className="opacity-70">Début</span>
                            <span>{fmtDate(sub.start_date)}</span>
                          </div>

                          <div className="mt-1 flex items-center justify-between">
                            <span className="opacity-70">Fin</span>
                            <span>{fmtDate(sub.end_date)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Parrainage (ta section existante) */}
        <ParrainageSection />

        <ToastContainer />
      </div>
    </section>
  );
}
